/**
 * The Hypothesis (SYR-90, Phase 2).
 *
 * Day-0 inferred-constraint hypothesis formed from the brand's public presence
 * (website + reviews) before any survey data exists. Always labelled as
 * inferred, never measured; shows live progress toward the response count that
 * resolves it (confirm/overturn — Phase 4). Renders nothing when no hypothesis
 * exists for the brand, so established accounts never see an empty shell.
 */

import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import {
  useDismissBrandHypothesisMutation,
  useGetBrandHypothesisQuery,
} from '../../../services/nestjs-base-api';
import type { HypothesisSignal } from '../../../services/nestjs-base-api/types';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { Card } from '../atoms';
import { BAND_PALETTE } from '../config/bandColours';
import { THEME } from '../config/theme';

const PURPLE = '#7C3AED';
const PURPLE_SOFT = '#F3EEFC';

// Phase 6C scorecard signal presentation (labels resolved via react-intl).
const SIGNAL_STYLES: Record<
  HypothesisSignal,
  { messageId: string; defaultLabel: string; fg: string; bg: string }
> = {
  STRENGTH: {
    messageId: 'marketerView.hypothesis.signal.strength',
    defaultLabel: 'Looks strong',
    fg: '#16A34A',
    bg: '#F0FDF4',
  },
  WATCH: {
    messageId: 'marketerView.hypothesis.signal.watch',
    defaultLabel: 'Worth watching',
    fg: '#D97706',
    bg: '#FFFBEB',
  },
  LIKELY_CONSTRAINT: {
    messageId: 'marketerView.hypothesis.signal.likelyConstraint',
    defaultLabel: 'Likely constraint',
    fg: PURPLE,
    bg: PURPLE_SOFT,
  },
  INSUFFICIENT_SIGNAL: {
    messageId: 'marketerView.hypothesis.signal.insufficient',
    defaultLabel: "Can't tell yet",
    fg: '#6B7280',
    bg: '#F9FAFB',
  },
};

export const HypothesisCard: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const { activeBrand } = useMarketerScope();
  const organisationId = user?.organisation?.id ?? 0;
  const jwtToken = user?.jwtToken ?? '';

  const { data } = useGetBrandHypothesisQuery(
    { jwtToken, organisationId, brandId: activeBrand?.id ?? 0 },
    { skip: !jwtToken || !organisationId || !activeBrand?.id },
  );
  const [dismissHypothesis] = useDismissBrandHypothesisMutation();
  const [dismissed, setDismissed] = useState(false);

  const isActive = data?.status === 'ACTIVE';
  const isResolved =
    data?.status === 'CONFIRMED' || data?.status === 'OVERTURNED';

  if (dismissed || !data?.exists || (!isActive && !isResolved)) return null;

  const handleDismiss = () => {
    // Hide immediately; the server call silences future hypothesis emails too.
    setDismissed(true);
    if (activeBrand?.id) {
      dismissHypothesis({ jwtToken, organisationId, brandId: activeBrand.id });
    }
  };

  const responseCount = data.responseCount ?? 0;
  const requiredResponses = data.requiredResponses ?? 30;
  const progress = Math.min(1, responseCount / requiredResponses);

  // Phase 4 — the reveal: measurement has confirmed or overturned the
  // hypothesis. Both read as wins (overturned = measurement beat inference).
  if (isResolved) {
    const confirmed = data.status === 'CONFIRMED';
    const accent = confirmed ? BAND_PALETTE.strong.fg : '#D97706';
    const accentSoft = confirmed ? '#DCFCE7' : '#FEF3C7';
    return (
      <section id="hypothesis" style={{ paddingTop: 8, paddingBottom: 20 }}>
        <Card pad={0} style={{ overflow: 'hidden', border: `1px solid ${accent}33` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr' }}>
            <div
              style={{
                background: accent,
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px 10px',
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' }}>
                HYPOTHESIS
              </span>
              <span style={{ fontSize: 12, fontWeight: 800, lineHeight: 1.2 }}>
                {confirmed ? 'CONFIRMED' : 'OVERTURNED'}
              </span>
            </div>
            <div style={{ padding: '22px 28px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    background: accentSoft,
                    color: accent,
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 999,
                    padding: '3px 10px',
                  }}
                >
                  <FormattedMessage
                    id="marketerView.hypothesis.measuredBadge"
                    defaultMessage="Measured with {count} customer responses"
                    values={{ count: data.responseCountAtResolution ?? requiredResponses }}
                  />
                </div>
                <span
                  role="button"
                  onClick={handleDismiss}
                  style={{
                    fontSize: 12.5,
                    color: THEME.textSecondary,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    paddingTop: 3,
                  }}
                >
                  <FormattedMessage
                    id="marketerView.hypothesis.dismiss"
                    defaultMessage="Dismiss"
                  />
                </span>
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: THEME.textBase, margin: 0 }}>
                {data.revealHeadline}
              </h3>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155', margin: '10px 0 0' }}>
                {data.revealBody}
              </p>
              <p style={{ fontSize: 12.5, color: THEME.textSecondary, margin: '14px 0 0' }}>
                <FormattedMessage
                  id="marketerView.hypothesis.originalNote"
                  defaultMessage="Original hypothesis, inferred from public data: {headline}"
                  values={{ headline: data.headline }}
                />
              </p>
            </div>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section id="hypothesis" style={{ paddingTop: 8, paddingBottom: 20 }}>
      <Card pad={0} style={{ overflow: 'hidden', border: `1px solid ${PURPLE}33` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr' }}>
          <div
            style={{
              background: PURPLE,
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 0',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em' }}>
              THE
            </span>
            <span
              style={{
                fontSize: 17,
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: '0.02em',
                textAlign: 'center',
              }}
            >
              HYPO&shy;THESIS
            </span>
          </div>
          <div style={{ padding: '22px 28px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  background: PURPLE_SOFT,
                  color: PURPLE,
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 999,
                  padding: '3px 10px',
                }}
              >
                <FormattedMessage
                  id="marketerView.hypothesis.inferredBadge"
                  defaultMessage="Inferred from public data, not yet measured"
                />
              </div>
              <span
                role="button"
                onClick={handleDismiss}
                style={{
                  fontSize: 12.5,
                  color: THEME.textSecondary,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  paddingTop: 3,
                }}
              >
                <FormattedMessage
                  id="marketerView.hypothesis.dismiss"
                  defaultMessage="Dismiss"
                />
              </span>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: THEME.textBase, margin: 0 }}>
              {data.headline}
            </h3>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155', margin: '10px 0 0' }}>
              {data.claim}
            </p>

            {(data.elementReadings?.length ?? 0) > 0 ? (
              // Phase 6C — the element scorecard: one signal per element,
              // the constraint row highlighted; reviews and competitor
              // evidence live in the per-row notes.
              <div style={{ marginTop: 16 }}>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: THEME.textBase,
                    margin: '0 0 8px',
                  }}
                >
                  <FormattedMessage
                    id="marketerView.hypothesis.scorecardTitle"
                    defaultMessage="How your brand reads across our elements"
                  />
                </p>
                <div style={{ border: `1px solid ${THEME.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  {data.elementReadings!.map((reading, i) => {
                    const style = SIGNAL_STYLES[reading.signal];
                    const isConstraint = reading.signal === 'LIKELY_CONSTRAINT';
                    return (
                      <div
                        key={reading.element}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'baseline',
                          gap: '4px 12px',
                          padding: '9px 14px',
                          background: isConstraint ? PURPLE_SOFT : i % 2 ? '#FAFAFC' : '#fff',
                          borderTop: i === 0 ? 'none' : `1px solid ${THEME.border}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: isConstraint ? 800 : 600,
                            color: THEME.textBase,
                            flex: '0 0 190px',
                          }}
                        >
                          {reading.element}
                        </span>
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: style.fg,
                            background: isConstraint ? '#fff' : style.bg,
                            borderRadius: 999,
                            padding: '2px 10px',
                            whiteSpace: 'nowrap',
                            flex: '0 0 auto',
                          }}
                        >
                          {intl.formatMessage({
                            id: style.messageId,
                            defaultMessage: style.defaultLabel,
                          })}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: THEME.textSecondary,
                            flex: '1 1 260px',
                            minWidth: 200,
                          }}
                        >
                          {reading.note}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {(data.competitorsAnalysed?.length ?? 0) > 0 && (
                  <p style={{ fontSize: 12.5, color: THEME.textSecondary, margin: '10px 0 0' }}>
                    <FormattedMessage
                      id="marketerView.hypothesis.competitors"
                      defaultMessage="Competitors analysed: {names}"
                      values={{ names: data.competitorsAnalysed!.join(', ') }}
                    />
                  </p>
                )}
              </div>
            ) : (
              (data.evidenceBullets?.length ?? 0) > 0 && (
                <ul style={{ margin: '14px 0 0', paddingLeft: 18 }}>
                  {data.evidenceBullets!.map((bullet, i) => (
                    <li
                      key={i}
                      style={{ fontSize: 14, lineHeight: 1.55, color: THEME.textSecondary, marginTop: 4 }}
                    >
                      {bullet}
                    </li>
                  ))}
                </ul>
              )
            )}

            {data.sourcesSummary && (
              <p style={{ fontSize: 12.5, color: THEME.textSecondary, margin: '12px 0 0' }}>
                <FormattedMessage
                  id="marketerView.hypothesis.sources"
                  defaultMessage="Based on {sources}."
                  values={{ sources: data.sourcesSummary }}
                />
              </p>
            )}

            <div
              style={{
                marginTop: 18,
                paddingTop: 16,
                borderTop: `1px solid ${THEME.border}`,
              }}
            >
              <p style={{ fontSize: 15, fontWeight: 600, color: THEME.textBase, margin: 0 }}>
                {data.closingQuestion}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                <div
                  style={{
                    flex: 1,
                    height: 8,
                    background: THEME.border,
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${progress * 100}%`,
                      height: '100%',
                      background: progress >= 1 ? BAND_PALETTE.strong.fg : PURPLE,
                      borderRadius: 999,
                      transition: 'width 300ms ease',
                    }}
                  />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: THEME.textSecondary, whiteSpace: 'nowrap' }}>
                  <FormattedMessage
                    id="marketerView.hypothesis.progress"
                    defaultMessage="{count} of {required} responses"
                    values={{ count: responseCount, required: requiredResponses }}
                  />
                </span>
              </div>
              <p style={{ fontSize: 13, color: THEME.textSecondary, margin: '8px 0 0' }}>
                <FormattedMessage
                  id="marketerView.hypothesis.resolveHint"
                  defaultMessage="Collect {required} responses to confirm or overturn this hypothesis with measured data."
                  values={{ required: requiredResponses }}
                />
              </p>
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
};
